import { action } from '@ember/object';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import BaseRoute from './base-route.gjs';
import Component from '@glimmer/component';
import ContactListItem from '../contact-list-item.gjs';
import GhostButton from 'ember-simplepractice/components/spds/action/button/ghost';
import SharedInfinityLoader from 'ember-simplepractice/components/shared/infinity-loader';
import TextInput from 'ember-simplepractice/components/spds/input/text';
import styles from './contacts.module.scss';
import svgJar from 'ember-svg-jar/helpers/svg-jar';

export default class MessagingRoutesContacts extends Component {
  @service messaging;
  @service messagingNavigation;

  get routeHandler() {
    return this.args.handler;
  }

  get search() {
    return this.routeHandler.search;
  }

  get isRecentContacts() {
    return this.routeHandler.isRecentContacts;
  }

  get contactsList() {
    if (!this.messaging.contacts || this.messaging.getContacts.isRunning) {
      return [];
    }

    const contacts = this.messaging.contacts;

    if (this.search.trim()) {
      return contacts;
    }

    // If no search, show recent clients as default
    return contacts.filter(contact => contact.isRecent);
  }

  @action
  leaveContactPage() {
    this.messagingNavigation.toConversationList();
  }

  @action
  updateSearch(event) {
    this.routeHandler.updateSearch(event.target.value);
  }

  @action
  clearSearch() {
    this.routeHandler.updateSearch('');
  }

  @action
  loadMoreContacts(model) {
    this.routeHandler.loadMoreContacts(model);
  }

  <template>
    <BaseRoute class="{{styles.component}} contacts-page" data-test-contacts-page>
      <:header>
        <GhostButton
          @fitContent={{true}}
          @iconOnly={{true}}
          {{on "click" this.leaveContactPage}}
          class="back-button"
          data-test-contacts-page-back-button
        >
          {{svgJar "messaging-chevron-left"}}
        </GhostButton>
        <span data-test-contacts-page-title>{{t "emc.contacts_page.new_message"}}</span>
      </:header>
      <:default>
        <div class="search-container" data-test-search-container>
          <label for="contactSearchInput" class="search-label">{{t
              "emc.contacts_page.search_input_label"
            }}</label>
          <TextInput
            class="search-input"
            @id="contactSearchInput"
            @value={{this.search}}
            @onInput={{this.updateSearch}}
            @placeholder={{t "emc.contacts_page.search_placeholder"}}
            data-test-search-input
          />
          {{#if this.search}}
            <button
              type="button"
              class="clear-search-button"
              aria-label={{t "emc.contacts_page.clear_search"}}
              {{on "click" this.clearSearch}}
              data-test-clear-search-button
            >
              {{svgJar "x-mark"}}
            </button>
          {{/if}}
          <div id="search-help-text" class="visually-hidden">
            {{t "emc.contacts_page.search_help_text"}}
          </div>
        </div>
        {{#if this.messaging.getContacts.isRunning}}
          <div
            class="spinner"
            data-test-loading-spinner
            aria-live="polite"
            aria-label={{t "emc.contacts_page.loading"}}
          >
            {{svgJar "messaging-spinner"}}
          </div>
        {{else if this.contactsList}}
          {{#if this.isRecentContacts}}
            <div class="recent-conversations-label" data-test-recent-contacts-label>
              {{t "emc.contacts_page.recent_conversations"}}
            </div>
          {{/if}}
          <ul
            class="contact-list-body"
            data-test-contact-list-body
            role="listbox"
            aria-label={{t "emc.contacts_page.list"}}
          >
            {{#each this.contactsList as |contact|}}
              <ContactListItem @contact={{contact}} />
            {{/each}}
            {{#if this.search}}
              <SharedInfinityLoader
                @model={{this.messaging.contacts}}
                @infinityLoad={{this.loadMoreContacts}}
                aria-label={{t "emc.contacts_page.infinity_loader_label"}}
              />
            {{/if}}
          </ul>
        {{else}}
          <div
            class="empty-contact-list"
            data-test-empty-contact-list
            role="status"
            aria-live="polite"
            aria-label={{t "emc.contacts_page.no_contacts_found"}}
          >
          </div>
        {{/if}}
      </:default>
    </BaseRoute>
  </template>
}
